import useUser from '@/redux/hooks/useUser';
import { useCallback, useMemo } from 'react';

export const ROLES = {
  ADMIN: 1,
  CUSTOMER_CARE: 2,
  DIGITAL: 3,
};

export const PERMISSIONS = {
  REPORT: [ROLES.ADMIN, ROLES.DIGITAL],
  PRIZE_MANAGEMENT: [ROLES.ADMIN],
  CUSTOMER_CARE: [ROLES.ADMIN, ROLES.CUSTOMER_CARE, ROLES.DIGITAL],
  USER_KEY_MANAGEMENT: [ROLES.ADMIN, ROLES.CUSTOMER_CARE, ROLES.DIGITAL],
  MISSIONS: [ROLES.ADMIN],
  TOPICS: [ROLES.ADMIN],
  TOPIC_ANSWERS: [ROLES.ADMIN],
  WHEEL: [ROLES.ADMIN],
  MSISDN_BLOCK: [ROLES.ADMIN],
  SEND_SMS: [ROLES.ADMIN],
  CONFIG: [ROLES.ADMIN],
  THEME_MANAGEMENT: [ROLES.ADMIN],
};

export const useRole = () => {
  const { user } = useUser();

  const hasRole = useCallback(
    (allowedRoles) => {
      if (!allowedRoles || allowedRoles.length === 0) return true;
      return allowedRoles.includes(user.role_id);
    },
    [user.role_id]
  );

  const hasPermission = useCallback(
    (permission) => {
      if (!permission || !PERMISSIONS[permission]) return false;
      return PERMISSIONS[permission].includes(user.role_id);
    },
    [user.role_id]
  );

  const canAccess = useCallback(
    (section) => {
      return hasPermission(section);
    },
    [hasPermission]
  );

  return useMemo(
    () => ({
      isAdmin: user.role_id === ROLES.ADMIN,
      isCustomerCare: user.role_id === ROLES.CUSTOMER_CARE,
      isDigital: user.role_id === ROLES.DIGITAL,
      userRole: user.role_id,
      hasRole,
      hasPermission,
      canAccess,
    }),
    [user.role_id, hasRole, hasPermission, canAccess]
  );
};
